<template>
  <v-row no-gutters class="pt-5 fill-height flex-column">
    <v-col class="pr-2" v-if="isFetched">
      <card-title-row translation-path="sidebar.developmentPlanning.title" class="pb-5" :has-buttons="false">
        <template #subtitle>
          <italic-subheader
            style="font-size: 12px !important;"
            class="text-left"
            translation-path="sidebar.developmentPlanning.subtitle"
          />
        </template>
      </card-title-row>
      <card-title-row
        custom-size="text-body-3 font-weight-medium"
        translation-path="sidebar.developmentPlanning.investments"
      >
        <template #buttons>
          <base-rounded-button
            icon="mdi-plus"
            size="x-small"
            color="success"
            :tooltip="$i18n.t('sidebar.developmentPlanning.addInvestment')"
            @click="openAddInvestmentDialog"
          />
        </template>
      </card-title-row>
      <sidebar-row border-bottom class="pb-7 pt-3">
        <ribbon-list :list-items="computedDictionary" :toggle-max-items="5" class="mb-7">
          <template #itemButtons-replace="{ item, index }">
            <v-col cols="auto">
              <dots-menu @delete="toggleDelete(item, index)" :items="dotsItems" font-size="19px" />
            </v-col>
          </template>
        </ribbon-list>
      </sidebar-row>
      <sidebar-row>
        <v-form v-model="isFormValid">
          <v-row v-for="input in inputsConfig" :key="input.name" no-gutters class="mb-10 justify-space-between">
            <v-col cols="6" class="text-body-2 center-content text-left pt-3"> {{ input.translation }}: </v-col>
            <v-col cols="6">
              <data-input v-bind="input.props" v-model="formParams[input.name]" />
            </v-col>
          </v-row>
        </v-form>
      </sidebar-row>
      <main-dialog
        v-if="isDeleteDialogLoaded"
        :is-visible.sync="isDeleteDialogVisible"
        :title="$i18n.t('dialog.title.deleteInvestment')"
        :body="$i18n.t('dialog.body.deleteInvestmentConfirmation', { name: deletingParameter.title })"
        :loading="isDeleteDialogLoading"
        agree-text="dialog.agreeDelete"
        @agree="handleDelete"
      />
      <main-input-dialog
        v-if="isMainInputDialogLoaded"
        :is-visible.sync="isMainInputDialogVisible"
        :loading="isMainInputDialogLoading"
        :title-translation="$i18n.t('dialog.title.addInvestment')"
        :inputs="mainInputDialogInputs"
        dialog-width="35%"
        agree-text="dialog.agreeAdd"
        @agree="handleAdd"
      >
        <template #[`investmentName.input`]="data">
          <v-col cols="6">
            <data-input v-bind="data" v-model="investmentName" />
          </v-col>
        </template>
      </main-input-dialog>
    </v-col>
    <main-loader v-else />
  </v-row>
</template>

<script>
import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import ItalicSubheader from '@/components/ItalicSubheader';
import MainLoader from '@/components/MainLoader';
import SidebarRow from '@/components/SidebarRow';
import RibbonList from '@/components/RibbonList';
import DotsMenu from '@/components/DotsMenu';
import profiles from '@/mixins/profiles';

import { get, sync, call } from 'vuex-pathify';

export default {
  name: 'NetDevelopmentPlanning',
  mixins: [profiles],
  components: {
    DotsMenu,
    CardTitleRow,
    RibbonList,
    DataInput,
    ItalicSubheader,
    MainLoader,
    SidebarRow,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  data: () => ({
    dotsItems: [{ name: 'delete' }],
    formParams: {
      modelingProfile: undefined,
      currentInvestment: undefined,
    },
    isFetched: false,
    isFormValid: false,
    investmentName: null,
    isDeleteDialogVisible: false,
    isDeleteDialogLoading: false,
    isDeleteDialogLoaded: false,
    isMainInputDialogLoaded: false,
    isMainInputDialogVisible: false,
    isMainInputDialogLoading: false,
    deletingParameter: undefined,
    netDevelopmentDictionary: null,
    deletingIndex: null,
  }),
  computed: {
    epanetProfiles: get('epanet/epanetProfiles'),
    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    stickyButtons: sync('sidebar/stickyButtons'),
    developmentPlanningAttribute: get('admin/modulesMapping@water.development_planning_attribute'),
    developmentPlanningDictId: get('admin/modulesMapping@water.development_planning_dict_id'),
    computedDictionary() {
      return this.netDevelopmentDictionary?.values.map(value => {
        return {
          title: value,
        };
      });
    },
    mainInputDialogInputs() {
      return [
        {
          name: 'investmentName',
          translationPath: 'sidebar.developmentPlanning.investmentName',
          dataType: {
            name: 'text',
          },
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
      ];
    },
    inputsConfig() {
      return [
        {
          translation: this.$i18n.t('sidebar.developmentPlanning.typeOfInvestment'),
          name: 'currentInvestment',
          props: {
            dataType: { name: 'select' },
            items: this.netDevelopmentDictionary?.values || [],
            rules: [v => !!v || v === 0 || this.$i18n.t('rules.required')],
            hideDetails: true,
          },
        },
        {
          translation: this.$i18n.t('sidebar.developmentPlanning.modelingProfile'),
          name: 'modelingProfile',
          props: {
            dataType: { name: 'select' },
            items: this.epanetProfiles,
            itemText: 'name',
            returnObject: true,
            rules: [v => !!v || v === 0 || this.$i18n.t('rules.required')],
            hideDetails: true,
          },
        },
      ];
    },
  },
  watch: {
    isFormValid: {
      immediate: true,
      handler(nV) {
        this.isStickyButtonsValid = nV;
      },
    },
  },
  methods: {
    getEpanetProfiles: call('epanet/getEpanetProfiles'),
    getSpecificDictionary: call('admin/getSpecificDictionary'),
    putDict: call('admin/putDictionary'),
    runEpanetModel: call('epanet/runModel'),
    async init() {
      try {
        await this.getEpanetProfiles();
        this.netDevelopmentDictionary = await this.getSpecificDictionary(this.developmentPlanningDictId);
        this.isStickyButtonsVisible = true;
        this.isStickyButtonsValid = this.isValid;
        this.stickyButtons = [
          {
            action: 'epanetAnalysisModel',
            translationPath: 'sidebar.model',
          },
        ];
        this.$root.$off('epanetAnalysisModel');
        this.$root.$on('epanetAnalysisModel', this.runModel);
      } finally {
        this.isFetched = true;
      }
    },
    openAddInvestmentDialog() {
      this.isMainInputDialogLoaded = true;
      this.isMainInputDialogVisible = true;
      this.investmentName = null;
    },
    toggleDelete(parameterData, index) {
      this.isDeleteDialogLoaded = true;
      this.isDeleteDialogVisible = true;
      this.deletingIndex = index;
      this.deletingParameter = parameterData;
    },
    async prepareAndEditDict(deleting = false) {
      const currentValues = [...this.netDevelopmentDictionary?.values];
      deleting ? currentValues.splice(this.deletingIndex, 1) : currentValues.push(this.investmentName);
      const formData = new FormData();
      formData.append('images', JSON.stringify(currentValues.map(() => null)));
      formData.append('colors', JSON.stringify(currentValues.map(() => null)));
      formData.append('values', JSON.stringify(currentValues));
      const emptyFile = new File([''], '');
      const pictures = currentValues.map(() => emptyFile);
      for (let picture of pictures) {
        formData.append('files', picture);
      }
      const data = { formData, id: this.netDevelopmentDictionary.id };
      await this.putDict(data);
      this.netDevelopmentDictionary = await this.getSpecificDictionary(this.developmentPlanningDictId);
    },
    async handleAdd() {
      this.isMainInputDialogLoading = true;
      try {
        await this.prepareAndEditDict();
        this.isMainInputDialogLoading = false;
        this.isMainInputDialogVisible = false;
      } catch {
        this.isMainInputDialogLoading = false;
      }
    },
    async handleDelete() {
      this.isDeleteDialogLoading = true;
      try {
        await this.prepareAndEditDict(true);
        this.isDeleteDialogVisible = false;
        this.isDeleteDialogLoading = false;
      } catch {
        this.isDeleteDialogLoading = false;
      }
    },
    getPayload() {
      const modelingProfile = this.getWaterModelingProfilePayload(this.formParams.modelingProfile);
      const payload = {
        ...modelingProfile,
        features_filter: [
          ...modelingProfile.features_filter,
          {
            filter: {
              '!OR': [
                {
                  $EQUAL: {
                    [`water_pipes.${this.developmentPlanningAttribute}`]: {
                      value: this.formParams.currentInvestment,
                    },
                  },
                },
                {
                  IS_NULL: `water_pipes.${this.developmentPlanningAttribute}`,
                },
              ],
            },
            datasource: 'water_pipes',
          },
        ],
      };
      return payload;
    },
    async runModel() {
      this.isStickyButtonsLoading = true;
      try {
        const payload = this.getPayload();
        const r = await this.runEpanetModel(payload);
        this.$router.push({
          name: 'modelingResultsScenario',
          params: {
            netType: 'water',
            scenarioId: r.scenario_id,
          },
        });
        localStorage.setItem(`scenarioId-water`, r.scenario_id);
      } finally {
        this.isStickyButtonsLoading = false;
      }
    },
  },
  beforeDestroy() {
    this.isStickyButtonsVisible = false;
    this.stickyButtons = [];
  },
  mounted() {
    this.init();
  },
};
</script>
